<template>
    <div class="widget-status-card-sounds space-y-2">
        <div class="main-text-color font-semibold">
            {{ $t('settings.soundWarning') }}
        </div>
        <collapse-transition>
            <div
                v-if="hasActiveNotifications"
                class="pt-4 border-t border-ui-lines"
            >
                <RedesignBaseButton
                    type="outline"
                    color="secondary"
                    size="small"
                    class="w-full"
                    @click="disableAllNotifications"
                >
                    <span class="flex items-center justify-center gap-2">
                        <RedesignBaseIcon
                            name="BellOff"
                            :size="16"
                        />
                        {{ $t('settings.disableAllNotifications') }}
                    </span>
                </RedesignBaseButton>
            </div>
        </collapse-transition>
        <div class="pt-3">
            <div class="text-sm mb-1">
                {{ $t('settings.thresholdDirection') }}
            </div>
            <div class="flex items-center gap-2">
                <RedesignSwitch
                    v-model="widgetLayoutModel.reverseThreshold"
                />
                <span class="text-sm text-gray-600">
                    {{ widgetLayoutModel.reverseThreshold
                        ? $t('widget.config.higherIsBetter')
                        : $t('widget.config.lowerIsBetter')
                    }}
                </span>
            </div>
        </div>
        <div>
            <RedesignFormLabel
                :label="$t('widget.config.maximumWarningRangeValue')"
                class="mb-2"
            />
            <RedesignInputNumber
                v-model="widgetLayoutModel.WarningMaxCounter"
                :max="100"
                :min="0"
                :step="1"
            />
        </div>
        <div>
            <RedesignFormLabel
                :label="$t('settings.warningColor')"
                class="mb-2"
            />
            <RedesignWidgetColor
                v-model="widgetLayoutModel.warningColor"
                :predefine="predefinedColors"
            />
        </div>
        <div>
            <RedesignFormLabel
                :label="$t('settings.warningSound')"
                class="mb-2"
            />
            <SoundSelect
                v-model="widgetLayoutModel.soundWarning"
                :placeholder="String($t('settings.warningSound'))"
                :options="warningSoundList"
                class="w-full"
            />
        </div>
        <div class="main-text-color font-semibold pt-3">
            {{ $t('settings.soundLimit') }}
        </div>
        <div>
            <RedesignFormLabel
                :label="$t('widget.config.maximumLimitRangeValue')"
                class="mb-2"
            />
            <RedesignInputNumber
                v-model="widgetLayoutModel.LimitMaxCounter"
                :max="100"
                :min="0"
                :step="1"
            />
        </div>
        <div>
            <RedesignFormLabel
                :label="$t('settings.limitColor')"
                class="mb-2"
            />
            <RedesignWidgetColor
                v-model="widgetLayoutModel.limitColor"
                :predefine="predefinedColors"
            />
        </div>
        <div>
            <RedesignFormLabel
                :label="$t('settings.limitSound')"
                class="mb-2"
            />
            <SoundSelect
                v-model="widgetLayoutModel.soundLimit"
                :placeholder="String($t('settings.limitSound'))"
                :options="errorSoundList"
                class="w-full"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { predefinedColors } from '@/views/DashboardSettings/LayoutManagement/layout-management'
import RedesignWidgetColor from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetColor.vue'
import SoundSelect from '@/components/Common/SoundSelect.vue'
import RedesignFormLabel from '@/components/RedesignBaseComponents/Form/RedesignFormLabel.vue'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignSwitch from '@/components/RedesignBaseComponents/Inputs/RedesignSwitch.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { WidgetLayout } from '@/types/dashboard'
import { useVModel } from '@vueuse/core'
import { errorSoundList, warningSoundList } from '@/composables/globalData/useEntitiesData'
import { computed } from 'vue'

const props =defineProps<{
    value: WidgetLayout
}>()

const emit = defineEmits<{
    (e: 'input', value: WidgetLayout): void
}>()

const widgetLayoutModel = useVModel(props, 'value', emit, {
    eventName: 'input'
})

const hasActiveNotifications = computed(() => {
    return (
        (widgetLayoutModel.value.WarningMaxCounter && widgetLayoutModel.value.WarningMaxCounter > 0) ||
        (widgetLayoutModel.value.LimitMaxCounter && widgetLayoutModel.value.LimitMaxCounter > 0)
    )
})
function disableAllNotifications () {
    widgetLayoutModel.value = {
        ...widgetLayoutModel.value,
        WarningMaxCounter: 0,
        LimitMaxCounter: 0,
        warningColor: '',
        limitColor: '',
        soundWarning: '',
        soundLimit: ''
    }
}
</script>

